import React, {
  createContext,
  useCallback,
  useState,
  ReactNode,
  useMemo,
} from "react";
import { Snackbar, Alert, AlertColor } from "@mui/material";

export type SnackbarMessage = {
  message: string;
  severity: AlertColor;
};

export type AppSnackbarContextType = {
  showSnackbar: (message: string, severity: AlertColor) => void;
};

export const AppSnackbarContext = createContext<AppSnackbarContextType | null>(
  null
);

export type AppSnackbarProviderProps = {
  children: ReactNode;
  /** Auto-hide duration in ms (default: 4000) */
  autoHideDuration?: number;
};

/**
 * Context provider for app-wide snackbar notifications.
 * Wrap your app (or a section) with this provider to enable
 * the useAppSnackbar hook.
 */
export const AppSnackbarProvider: React.FC<AppSnackbarProviderProps> = ({
  children,
  autoHideDuration = 4000,
}) => {
  const [open, setOpen] = useState(false);
  const [snackbarData, setSnackbarData] = useState<SnackbarMessage>({
    message: "",
    severity: "info",
  });

  const showSnackbar = useCallback(
    (message: string, severity: AlertColor) => {
      setSnackbarData({ message, severity });
      setOpen(true);
    },
    []
  );

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      // Don't close on clickaway - only on timeout or explicit close
      if (reason === "clickaway") {
        return;
      }
      setOpen(false);
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      showSnackbar,
    }),
    [showSnackbar]
  );

  return (
    <AppSnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbarData.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarData.message}
        </Alert>
      </Snackbar>
    </AppSnackbarContext.Provider>
  );
};

export default AppSnackbarProvider;
